/**
 * Where a sign-in may send someone afterwards (CHAP-594 review: the open
 * redirect). One rule for every consumer: the password sign-in and the
 * magic-link landing in the client, and the OIDC callback on the server.
 *
 * A return target is followed only when it is a same-origin relative path:
 * one leading '/', then anything but '/' or '\'. Either of those makes the
 * browser read a host ('//evil.example', '/\evil.example'), and a string that
 * starts with '/' has no scheme. Any ASCII control character is refused too:
 * the URL parser deletes tabs and newlines before it reads, so
 * '/\t/evil.example' would still become '//evil.example'. Anything else goes
 * to the flow's default page.
 */
export function safeReturnPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  if (!/^\/(?![/\\])/.test(raw)) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  return raw;
}

/** Where the sign-in page keeps the return path for the magic-link landing. */
export const RETURN_TO_KEY = 'cb_return_to';

/**
 * The sign-in page's returnTo query value, decoded once more as that page
 * always has, then checked. Null when the decode fails or the path is unsafe.
 */
export function returnPathFromQuery(param: string | null | undefined): string | null {
  if (!param) return null;
  try {
    return safeReturnPath(decodeURIComponent(param));
  } catch {
    return null;
  }
}

interface ReturnPathStorage {
  getItem(key: string): string | null;
  removeItem(key: string): void;
}

/**
 * The magic-link landing's read of the stored return path: always removed,
 * and followed only when safe, so a value stored before this check is not
 * trusted either.
 */
export function takeStoredReturnPath(storage: ReturnPathStorage): string | null {
  const raw = storage.getItem(RETURN_TO_KEY);
  storage.removeItem(RETURN_TO_KEY);
  return safeReturnPath(raw);
}
